import { AddIcon, CheckIcon, Grid, GridColumn, GridRow, Modal, SpinnerIcon } from '@bp/ui-components';
import { BpCalendar } from '../../../../components/BpCalendar/BpCalendar';
import { BpCard } from '../../../../components/BpCard/BpCard';
import { MeetingsLinks } from '../../../../components/MeetingsLinks/MeetingsLinks';
import { TeachingUnitsTree } from '../../../../components/TeachingUnitsTree/TeachingUnitsTree';
import React, { Suspense, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { showErrorToast } from '../../../../utils/showErrorToast';
import { useAuthClaims } from '../../../../hooks/useAuthClaims';
import { useCourseCalendarEvents } from '../../../../hooks/useCalendarEvents';
import { AnnouncementsList } from '../../../../components/AnnouncementsList/AnnouncementsList';
import { useGetMatrixRoomByName } from '../../../../hooks/matrix/useGetMatrixRoomByName';
import { NotificationReason, RoomNames } from '../../../../utils/matrixClient';
import { AnnouncementForm, AnnouncementFormType } from '../../../../components/AnnouncementForm/AnnouncementForm';
import { useGetAnnouncements } from '../../../../hooks/matrix/useGetAnnouncements';
import {
  ActiveAssignmentsTable,
  ActiveAssignmentsTableType,
} from '../../../../components/ActiveAssignmentsTable/ActiveAssignmentsTable';
import { uniqueByUuid } from '../../../../utils/uniquByUuid';
import { SendMatrixMessageForm } from '../../../../components/SendMatrixMessageForm/SendMatrixMessageForm';
import { TeachingUnitContext } from '../../../../context/TeachingUnitContext';
import dayjs from 'dayjs';
import { NotificationCountType } from 'matrix-js-sdk';
import { hasBlockingEvents } from '../../../../utils/hasBlockingEvents';
import { backendApi } from '../../../../utils/backendApi';
import { graphql } from 'client/gql';
import { useQuery } from 'urql';
import { usePermissionChecker } from 'hooks/usePermissionChecker';
import { CONNECT_DATE_TEMPLATE } from 'utils/connectLib';
import { SubmissionStatus, UserStatus } from 'client/gql/graphql';
import { useMatrixClient } from 'hooks/matrix/useMatrixClient';

const CourseOverviewTeacherContentPageQueryDocument = graphql(`
  query CourseOverviewTeacherContentPageData(
    $profile: ID!
    $courseUuid: ID!
    $groupUuid: ID!
    $organizationUuid: ID!
    $formattedDate: String!
  ) {
    groups(where: { uuid: $courseUuid, type: course }) {
      uuid
      __typename
      name
      organization {
        uuid
        __typename
      }
      viewers {
        uuid
        __typename
        userStatus
      }
      teachingUnits {
        uuid
        __typename
        assignments {
          uuid
          __typename
          dueDate {
            uuid
            __typename
            date
          }
          visibleFrom {
            uuid
            __typename
            date
          }
          title
          submissions {
            uuid
            __typename
            status
          }
        }
      }
    }
    ...PermissionCheckerFields
    ...UseAvailableServicesForOrganizationFields
    ...MeetingsLinksFields
  }
`);

type CourseOverviewTeacherContentProps = {
  courseUuid: string;
};

export const CourseOverviewTeacherContent = ({ courseUuid }: CourseOverviewTeacherContentProps) => {
  const { t } = useTranslation();
  const { pimAuthClaims } = useAuthClaims();
  const profileUuid = pimAuthClaims.getProfile().uuid;
  const organizationUuid = pimAuthClaims.getOrganization().uuid;

  const [announcementModal, setAnnouncementModal] = useState(false);
  const [messageModal, setMessageModal] = useState(false);

  const { getRoom } = useGetMatrixRoomByName();
  const { markAsRead, client } = useMatrixClient();

  const navigate = useNavigate();

  const [{ data, error }] = useQuery({
    query: CourseOverviewTeacherContentPageQueryDocument,
    variables: {
      profile: profileUuid,
      courseUuid,
      groupUuid: courseUuid,
      organizationUuid,
      formattedDate: dayjs().format(CONNECT_DATE_TEMPLATE),
    },
  });

  const perms = usePermissionChecker(data);

  const currentCourse = data?.groups[0];
  const teachingUnits = currentCourse?.teachingUnits;

  const room = getRoom(`${currentCourse?.organization.uuid}_${courseUuid}`, RoomNames.Group);
  const _hasUnread = room ? room.getRoomUnreadNotificationCount(NotificationCountType.Total) > 0 : false;

  const [hasUnread, setHasUnread] = useState<boolean>(false);

  useEffect(() => {
    setHasUnread(_hasUnread);
  }, [_hasUnread]);
  const [loading, setLoading] = useState(false);
  const announcements = useGetAnnouncements(room);

  const events = useCourseCalendarEvents(courseUuid ?? '');

  const { teachingUnitUuid, setTeachingUnitUuid } = useContext(TeachingUnitContext);

  const groupSubject = { uuid: courseUuid ?? '', organization: { uuid: currentCourse?.organization.uuid ?? '' } };

  const viewers = currentCourse?.viewers ?? [];
  const receiverProfiles = uniqueByUuid(
    viewers?.filter((profile) => {
      return (
        profile.uuid !== pimAuthClaims.getProfile().uuid &&
        (profile.userStatus === UserStatus.Full || profile.userStatus === UserStatus.ImpersonateOnly)
      );
    }),
  );

  const assignments: ActiveAssignmentsTableType[] = !error
    ? (teachingUnits
        ?.flatMap(({ assignments }) => assignments ?? [])
        .filter(
          (a) =>
            dayjs(a.dueDate?.date).isSameOrAfter(dayjs(), 'day') &&
            dayjs(a.visibleFrom?.date).isSameOrBefore(dayjs(), 'day'),
        )
        .map((assignment) => {
          return {
            title: assignment.title,
            uuid: assignment.uuid,
            openSubmissionsCount: assignment.submissions.filter((s) => {
              return s.status !== SubmissionStatus.New && s.status !== SubmissionStatus.Done;
            }).length,
          };
        }) ?? [])
    : [];

  const handleAnnouncementFormSubmit = async (values?: AnnouncementFormType) => {
    if (values) {
      try {
        setLoading(true);
        const roomId = room?.summary?.roomId;

        if (!roomId) {
          throw new Error('Could not find room');
        }

        if (!client) {
          throw new Error('Could not get Matrix Client');
        }

        if (!currentCourse?.name) {
          throw new Error('Could not get coursename');
        }

        const resp = await backendApi.notify(
          {
            type: NotificationReason.CourseAnnouncement,
            content: values.message,
            title: values.title,
            groupUuid: courseUuid,
          },
          { roomId, groupName: currentCourse?.name, client },
        );

        if (!resp.ok) throw new Error(resp.statusText);

        setAnnouncementModal(false);
      } catch (error) {
        showErrorToast({ name: '', graphQLErrors: [], message: error as string });
      } finally {
        setLoading(false);
      }
    }
  };

  error && showErrorToast(error);

  return (
    <>
      <Grid>
        <GridRow mobileGap='var(--grid-column-gap)'>
          <GridColumn width={8}>
            <BpCard
              className='mb-7'
              noPadding
              fixedHeight='360px'
              header={{ headline: t('assignments.active'), subHeadline: assignments?.length.toString() }}
            >
              <ActiveAssignmentsTable
                data={assignments}
                onClick={(uuid) => navigate(`/courses/${courseUuid}/assignments/${uuid}`)}
              />
            </BpCard>
            <BpCard
              canScroll
              fixedHeight='360px'
              noPadding
              hideActionTextBreakpoint='medium'
              header={{
                headline: t('announcements.title'),
                actions:
                  perms?.canCreateCourseAnnouncement(groupSubject) && room
                    ? [
                        {
                          text: t('common.mark'),
                          hierarchy: 'ghost',
                          disabled: !hasUnread,
                          icon: <CheckIcon />,
                          callback: async () => {
                            setHasUnread(false);
                            await markAsRead(room);
                          },
                        },
                        {
                          text: t('announcements.new'),
                          icon: <AddIcon />,
                          callback: () => {
                            setAnnouncementModal(true);
                          },
                        },
                      ]
                    : [
                        {
                          text: t('announcements.new'),
                          icon: <AddIcon />,
                          disabled: true,
                        },
                      ],
              }}
            >
              <AnnouncementsList
                announcements={announcements}
                hasPermissionToDelete={perms?.canCreateCourseAnnouncement(groupSubject) ?? false}
              />
            </BpCard>
          </GridColumn>
          <GridColumn width={4}>
            <BpCard header={{ headline: t('teachingUnits.title') }} className='mb-7' alwaysCollapsible>
              <TeachingUnitsTree
                courseUuid={courseUuid}
                activeTeachingUnitUuid={teachingUnitUuid}
                onTeachingUnitSelect={(uuid: string) => {
                  setTeachingUnitUuid(uuid);
                  navigate(`/courses/${courseUuid}/lessons/${uuid}`);
                }}
              />
            </BpCard>
            <BpCard className='pb-6 pr-6 pl-6 mb-7' noPadding showBorder hideBackground>
              <BpCalendar
                events={events}
                showAddButton={!!courseUuid}
                onAdd={() => navigate(`/courses/${courseUuid}/appointments/create`)}
                eventClick={() => navigate(`/courses/${courseUuid}/appointments`)}
              />
            </BpCard>
            <MeetingsLinks
              groupUuid={courseUuid}
              query={data}
              hasBlockingEvents={hasBlockingEvents(events)}
              onLessonClick={() => navigate('/meetings/planned')}
              onMessageClick={() => setMessageModal(true)}
            />
          </GridColumn>
        </GridRow>
      </Grid>

      {room && (
        <Modal
          isOpen={announcementModal}
          onRequestClose={() => setAnnouncementModal(false)}
          isFormModal
          hideFooter
          title={t('announcements.new')}
          width='s'
        >
          <Suspense fallback={<SpinnerIcon className={'svg-icon large spinning'} />}>
            <AnnouncementForm
              loading={loading}
              onSubmit={handleAnnouncementFormSubmit}
              closeModal={() => setAnnouncementModal(false)}
            />
          </Suspense>
        </Modal>
      )}

      {receiverProfiles && (
        <Modal
          isOpen={messageModal}
          onRequestClose={() => setMessageModal(false)}
          isFormModal
          hideFooter
          width={'s'}
          title={t('common.sendToAllCourseViewers')}
        >
          <Suspense fallback={<SpinnerIcon className={'svg-icon large spinning'} />}>
            <SendMatrixMessageForm onClose={() => setMessageModal(false)} groupUuid={courseUuid} />
          </Suspense>
        </Modal>
      )}
    </>
  );
};
